/* eslint-disable max-lines */
import {
    NinchatAttributes,
    NinchatProperties,
    NinchatState,
} from 'api/types'
import {
    NinchatAudienceTranscriptsStore,
    NinchatStoreUserChannel,
    NinchatUsersStore,
} from 'api/types/store'
import {
    getAudienceChannelUserId,
    isAudienceChannel,
    isScheduledAppointment,
} from '../../utils/channels'
import {
    getProtectedEmail,
    isProtectedEmailLoading,
} from 'utils/email'
import {
    isEqual,
    isObjectEmpty,
} from 'utils/props'
import {
    useAppDispatch,
    useAppSelector,
    useAppSelectorWithShallowEquality,
} from '../index'
import {NinchatUserAttributes} from '../../api/types/attributes'
import {cacheService} from '../../utils/cache.service'
import {describeChannel} from '../../api/action/describeChannel'
import {describeQueue} from '../../api/action/describeQueue'
import {describeTags} from '../../api/action/describeTags'
import {describeTranscript} from 'api/action/describeTranscript'
import {describeUserBuffer} from 'utils/bufferQueue'
import {useEffect} from 'react'
import {useSelector} from 'react-redux'
import {useTranslation} from 'react-i18next'
export {useRealm} from './useRealm'
export {useRealms} from './useRealms'


export const useQueues = (queue_ids: NinchatProperties.QueueID[], onlyMember = true): NinchatProperties.NinchatUserQueues => {
    const user_queues = useAppSelector(({ninchat}) => ninchat.user_queues || {}, (next, prev) => queue_ids.every((queue_id) => next[queue_id] === prev[queue_id]))
    const _queues = useAppSelector(
        ({ninchat}) => (onlyMember ? {} : ninchat._queues || {}),
        (next, prev) => (onlyMember ? true : queue_ids.every((queue_id) => next[queue_id] === prev[queue_id])),
    )

    const queues = {
        ...user_queues,
        ..._queues,
    }

    const dispatch = useAppDispatch()

    useEffect(() => {
        const toBeProcessedQueues = queue_ids.filter((id) => !cacheService.has(id))

        toBeProcessedQueues
            .filter((id) => Boolean(id))
            .filter((queue_id) => !(queue_id in queues) || !queues[queue_id].queue_members)
            .forEach((queue_id) => {
                cacheService.add(queue_id)
                dispatch(describeQueue({queue_id}))
            })
    })

    return Object.fromEntries(Object.entries(Object.assign({}, queues)).filter(([queue_id]) => queue_ids.includes(queue_id))) // eslint-disable-line prefer-object-spread
}

export const useQueueName = (queue_id: NinchatProperties.QueueID): string | undefined => {
    const dispatch = useAppDispatch()
    const queueName = useAppSelector(({ninchat}) => ninchat.user_queues?.[queue_id]?.queue_attrs?.name ?? ninchat._queues?.[queue_id]?.queue_attrs?.name)
    useEffect(() => {
        if (!cacheService.has(queue_id)) {
            cacheService.add(queue_id)
            dispatch(describeQueue({queue_id}))
        }
    }, [queue_id])
    return queueName
}

export const useQueueAttributes = (queue_id?: NinchatProperties.QueueID): NinchatAttributes.NinchatQueueAttributes | undefined => {
    const dispatch = useAppDispatch()
    const queue_attrs = useAppSelector(({ninchat}) => {
        const attrs = ninchat.user_queues?.[queue_id ?? '']?.queue_attrs ?? {}
        if (isObjectEmpty(attrs)) {
            return ninchat._queues?.[queue_id ?? '']?.queue_attrs ?? {}
        }
        return attrs
    })
    useEffect(() => {
        if (queue_id && !cacheService.has(queue_id)) {
            cacheService.add(queue_id)
            dispatch(describeQueue({queue_id}))
        }
    }, [queue_id])
    return queue_attrs ?? {}
}

export const useTags = (realm_id: NinchatProperties.RealmID | undefined): NinchatProperties.NinchatTagChildren => {
    const tags = useAppSelectorWithShallowEquality(({ninchat}: {ninchat: NinchatState}) => (realm_id ? ninchat._tags[realm_id] : {}))

    const dispatch = useAppDispatch()

    useEffect(() => {
        const hasTags = Object.keys(tags ?? {}).length > 0
        if (realm_id && !hasTags && !cacheService.has(realm_id, 'tag')) {
            cacheService.add(realm_id, 'tag')
            dispatch(describeTags({realm_id}))
        }
    })

    return tags ?? {}
}

export const useAudienceTranscripts = (audience_ids: NinchatProperties.AudienceID[]): NinchatAudienceTranscriptsStore => {
    const _user_audience_transcripts = useAppSelector(({ninchat}) => ninchat._user_audience_transcripts || {},
        (next, prev) => audience_ids.every((audience_id) => next[audience_id] === prev[audience_id]))

    const _audience_transcripts = useAppSelector(({ninchat}) => ninchat._audience_transcripts || {},
        (next, prev) => audience_ids.every((audience_id) => next[audience_id] === prev[audience_id]))

    const transcripts = {
        ..._user_audience_transcripts,
        ..._audience_transcripts,
    }

    const dispatch = useAppDispatch()

    useEffect(() => {
        const toBeProcessedTranscripts = audience_ids.filter((id) => !cacheService.has(id))
        toBeProcessedTranscripts
            .filter((id) => Boolean(id))
            .filter((audience_id) => !(audience_id in transcripts))
            .forEach((audience_id) => {
                cacheService.add(audience_id)
                dispatch(describeTranscript({audience_id}))
            })
    })

    return Object.fromEntries(Object.entries(Object.assign({}, transcripts)).filter(([audience_id]) => audience_ids.includes(audience_id))) // eslint-disable-line prefer-object-spread
}

export const useUsers = (user_ids: NinchatProperties.UserID[]): NinchatUsersStore => {
    const user = useSelector(({ninchat}: {ninchat: NinchatState}) => {
        if (ninchat.user_id && user_ids.includes(ninchat.user_id)) {
            return {
                [ninchat.user_id]: {
                    user_attrs: ninchat.user_attrs,
                    user_identities: ninchat.user_identities,
                },
            }
        }

        return {}
    }, (next, prev) => next === prev)

    const users = useSelector(({ninchat}: {ninchat: NinchatState}) => ninchat._users, (next, prev) => user_ids.every((user_id) => next[user_id] === prev[user_id]))

    const dispatch = useAppDispatch()

    useEffect(() => {
        describeUserBuffer.setDispatch(dispatch)
    }, [])

    useEffect(() => {
        const toBeProcessedUsers = user_ids.filter((id) => !cacheService.has(id))
        toBeProcessedUsers
            .filter((id) => Boolean(id))
            .filter((user_id) => !(user_id in users) && !(user_id in user))
            .forEach((user_id) => {
                describeUserBuffer.enqueue([user_id])
            })
    })

    return Object.fromEntries(Object.entries(Object.assign({}, users, user)).filter(([user_id]) => user_ids.includes(user_id))) // eslint-disable-line prefer-object-spread
}

export const useEmailIdentity = (user_id: NinchatProperties.UserID): string | undefined | 'loading' => {
    const dispatch = useAppDispatch()
    const currentLoggedInUserId = useAppSelector(({ninchat}) => ninchat.user_id)

    const emailIdentity = useAppSelector(({ninchat}) => {
        if (currentLoggedInUserId === user_id) {
            return getProtectedEmail(ninchat.user_identities)
        }
        return isProtectedEmailLoading(ninchat._users[user_id]) ? 'loading' : getProtectedEmail(ninchat._users[user_id]?.user_identities)
    })

    useEffect(() => {
        describeUserBuffer.setDispatch(dispatch)
    }, [])

    useEffect(() => {
        if (user_id && !cacheService.has(user_id) && currentLoggedInUserId !== user_id) {
            describeUserBuffer.enqueue([user_id])
        }
    }, [user_id])

    return emailIdentity
}

export const useChannels = (channelIds: Array<NinchatProperties.ChannelID>): NinchatState['user_channels'] => {
    const userChannels = useAppSelector(({ninchat}) => ninchat.user_channels, (next, prev) => channelIds.every((channelId) => next[channelId] === prev[channelId]))
    const dispatch = useAppDispatch()
    const shouldDescribeChannel = (currentChannel: NinchatStoreUserChannel): boolean => {
        if (currentChannel?.channel_members) {
            return false
        }
        return true
    }
    useEffect(() => {
        const tobeProcessedChannels = channelIds.filter((id) => !cacheService.has(id))
        tobeProcessedChannels
            .filter((id) => Boolean(id))
            .filter((id) => shouldDescribeChannel(userChannels?.[id]))
            .forEach((value) => {
                cacheService.add(value)
                dispatch(describeChannel({channel_id: value}))
            })
    }, [userChannels])

    return Object.entries(userChannels).filter(([channelId, _]) => channelIds.includes(channelId))
        .reduce((obj: NinchatState['user_channels'], [key, value]) => ({
            ...obj,
            [key]: value as NinchatStoreUserChannel,
        }), {})
}

export const useChannel = (channelId: NinchatProperties.ChannelID): NinchatStoreUserChannel => {
    const userChannel = useAppSelector(({ninchat}) => ninchat.user_channels?.[channelId], isEqual)
    const dispatch = useAppDispatch()

    const shouldDescribeChannel = (currentChannel: NinchatStoreUserChannel): boolean => {
        if (currentChannel?.channel_members) {
            return false
        }
        return true
    }

    useEffect(() => {
        if (channelId && !cacheService.has(channelId) && shouldDescribeChannel(userChannel)) {
            cacheService.add(channelId)
            dispatch(describeChannel({channel_id: channelId}))
        }
    }, [channelId])

    return userChannel ?? {}
}

export const useUserDetails = ({
    userId = '', dialogueStatus,
}: {
    userId?: NinchatProperties.UserID, dialogueStatus?: NinchatProperties.DialogueStatus
}): NinchatUserAttributes & {isSelected: boolean, dialogueStatus?: NinchatProperties.DialogueStatus, userFound?: boolean} => {
    const {t} = useTranslation()
    const currentUser = useUsers([userId].filter((id) => Boolean(id)))?.[userId]
    const isSelected = !!userId && history.state?.route?.params?.dialogue_id === userId
    if (currentUser) {
        const {user_attrs = {}} = currentUser
        return {
            admin: user_attrs.admin,
            connected: user_attrs.connected,
            deleted: user_attrs.deleted,
            dialogueStatus,
            guest: user_attrs.guest,
            iconurl: user_attrs.iconurl,
            idle: user_attrs.idle,
            info: user_attrs.info,
            isSelected,
            name: user_attrs.name
                ? user_attrs.name
                : user_attrs.deleted
                    ? t('Deleted user')
                    : t('Unnamed user'),
            realname: user_attrs.realname,
            userFound: true,
        }
    }
    return {
        dialogueStatus,
        isSelected,
    }
}

export const useChannelDetails = ({channelId = ''}: {
    channelId?: NinchatProperties.ChannelID,
}): ReturnType<typeof useUserDetails> & {isClosed?: boolean, isVideoChannel?: boolean, isVideoActive: boolean,
    isAudienceChannel: boolean, channelStatus?: NinchatProperties.ChannelStatus, createTime?: NinchatProperties.Time, queueId?: string, realmId?: string} => {
    const {t} = useTranslation()
    const currentChannel = useChannel(channelId)
    const channelUserId = getAudienceChannelUserId(currentChannel)
    const currentUser = useUserDetails({userId: channelUserId})
    const isVideoActive = useAppSelector(({jitsi}) => !!channelId && jitsi.activeVideoChannelId === channelId)

    const isSelected = !!channelId && history.state?.route?.params?.channel_id === channelId

    if (isAudienceChannel(currentChannel)) {
        if (isScheduledAppointment(currentChannel)) {
            return {
                channelStatus: currentChannel.channel_status,
                createTime: currentChannel.channel_attrs?.create_time,
                isAudienceChannel: true,
                isClosed: currentChannel.channel_attrs?.closed === true,
                isSelected,
                isVideoActive,
                isVideoChannel: currentChannel?.channel_attrs?.video === 'group',
                name: currentChannel.channel_attrs?.closed ? t('Ended scheduled appointment') : currentChannel.channel_attrs?.name ? currentChannel.channel_attrs?.name : t('Scheduled appointment'),
                queueId: currentChannel.channel_attrs?.queue_id,
                realmId: currentChannel.realm_id,
            }
        }
        if (currentUser.userFound) {
            return {
                ...currentUser,
                channelStatus: currentChannel.channel_status,
                createTime: currentChannel.channel_attrs?.create_time,
                isAudienceChannel: true,
                isClosed: currentChannel.channel_attrs?.closed === true,
                isSelected,
                isVideoActive,
                isVideoChannel: currentChannel?.channel_attrs?.video === 'group',
                queueId: currentChannel.channel_attrs?.queue_id,
                realmId: currentChannel.realm_id,
            }
        }
        return {
            channelStatus: currentChannel.channel_status,
            createTime: currentChannel.channel_attrs?.create_time,
            isAudienceChannel: true,
            isClosed: currentChannel.channel_attrs?.closed === true,
            isSelected,
            isVideoActive,
            isVideoChannel: currentChannel?.channel_attrs?.video === 'group',
            name: currentChannel.channel_attrs?.closed ? t('Ended audience') : t('Unknown user'),
            queueId: currentChannel.channel_attrs?.queue_id,
            realmId: currentChannel.realm_id,
        }
    }
    return {
        channelStatus: currentChannel.channel_status,
        createTime: currentChannel.channel_attrs?.create_time,
        isAudienceChannel: false,
        isClosed: currentChannel.channel_attrs?.closed === true,
        isSelected,
        isVideoActive,
        isVideoChannel: currentChannel?.channel_attrs?.video === 'group',
        name: currentChannel.channel_attrs?.name ? currentChannel.channel_attrs?.name : t('Unnamed channel'),
        realmId: currentChannel.realm_id,
    }
}
